import React, { useRef, useState, useCallback, useEffect, useMemo, useDeferredValue } from 'react';
import { MoldOrder, UserPreference } from '../../types/mold';
import { useNavigate } from 'react-router-dom';
import TableComponent from '../shared/TableComponent';
import ButtonGroup from '../shared/ButtonGroup';
import { useViewMode } from '../../hooks/useViewMode';
import MoldOrderCardView from './MoldOrderCardView';
import { ViewMode } from '../MoldList/types';
import { usePreferences } from '../../context/PreferencesContext';
import { useAppState } from '../../context/AppStateContext';
import { getMoldOrdersColumnGroups, getMoldOrdersTableColumns, GenericColumn } from '../KanbanBoard/utils/tableColumns';
import { getDefaultVisibleColumnsForTable } from '../KanbanBoard/utils/columnConfigurations';
import ColumnSelector from '../shared/ColumnSelector';
import { downloadCSV } from '../../utils/exportUtils';
import { searchMoldOrder } from '../../utils/searchUtils';
import { sortByPriority } from '../../utils/priority';
import PaginationControls from '../MoldList/PaginationControls';
import { MoldOrderSearchSortBy, MoldOrderSearchSortDir } from '../../types/moldOrder';
import {
  Download,
  Grid,
  Inbox,
  List,
  LoaderCircle,
  MoreHorizontal,
  SearchX,
  Settings,
} from 'lucide-react';

// Maps the server's sortBy enum (backend/api_spec.yml GetMoldOrdersSearchParamsSortBy) to the
// client column `key` that represents the same data (tableColumns.tsx: 'mold' shows
// mold.sbiId, 'orderDetail' shows orderDetail.dueDate). Only these columns are sortable in
// server mode (plan B6). operationDate is the one derived column promoted into SQL — its sort
// key mirrors derived.operationDate (backend derivedfields / lateral join).
const SERVER_SORT_TO_COLUMN_KEY: Record<MoldOrderSearchSortBy, string> = {
  id: 'id',
  createdAt: 'createdAt',
  priority: 'priority',
  dueDate: 'orderDetail',
  status: 'status',
  type: 'type',
  moldSbiId: 'mold',
  operationDate: 'operationDate',
};
const COLUMN_KEY_TO_SERVER_SORT: Record<string, MoldOrderSearchSortBy> = Object.fromEntries(
  Object.entries(SERVER_SORT_TO_COLUMN_KEY).map(([sortBy, key]) => [key, sortBy as MoldOrderSearchSortBy]),
);

export interface MoldOrderListServerModeProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  sortBy: MoldOrderSearchSortBy;
  sortDir: MoldOrderSearchSortDir;
  onSortChange: (key: MoldOrderSearchSortBy, dir: MoldOrderSearchSortDir) => void;
  loading?: boolean;
  /** Whether any server-side filter/search is active — drives the filter-aware empty state. */
  hasActiveFilters?: boolean;
  /** Clear all server-side filters/search (shown as an action in the empty state). */
  onClearFilters?: () => void;
  /** Server-generated CSV download (decision #2, B3) — replaces the client downloadCSV path. */
  onDownloadCsv: () => void | Promise<void>;
  /** Prevent duplicate exports and show progress while the server-generated CSV is loading. */
  downloadingCsv?: boolean;
}

interface MoldOrderListProps {
  moldOrders: MoldOrder[];
  preferences: UserPreference;
  // Search props - these will be passed from the container
  searchTerm?: string;
  selectedSearchField?: string;
  onSearchTermChange?: (term: string) => void;
  onSearchFieldChange?: (field: string) => void;
  bgColorCode?: string;
  // Preference context - allows different instances to save separate preferences
  preferenceContext?: 'default' | 'moldDetail';
  CustomHeader?: React.FC;
  hidePadding?: boolean;
  // When set, the list renders in server mode (mold-order-list-v2 plan §5.5): `moldOrders` is
  // already the current page, already server-filtered/sorted/searched. Client mode (the
  // default, used by MoldOrderListContainer + MoldDetail) is completely unaffected when this
  // prop is absent.
  serverMode?: MoldOrderListServerModeProps;
  // Opt-in fit-to-viewport (server mode only): on lg+ the list fills its parent's height and the
  // table body / card list scrolls internally, keeping the toolbar + pager pinned. Requires a
  // height-bounded parent (MainContainer fitToViewport). No effect on mobile or client mode.
  fillHeight?: boolean;
}

const MoldOrderList: React.FC<MoldOrderListProps> = ({
  moldOrders,
  preferences,
  searchTerm = '',
  selectedSearchField = 'all',
  bgColorCode = 'gray',
  preferenceContext = 'default',
  CustomHeader,
  hidePadding = false,
  serverMode,
  fillHeight = false,
}) => {
  const navigate = useNavigate();
  const { updatePreference } = usePreferences();
  const { getStatusText, companyCode, getMoldOrderTypeLabel } = useAppState();
  
  // Get preference values based on context
  const contextPreferences = preferenceContext === 'moldDetail' ? preferences.moldPage : preferences;
  const tableTypeConfig = preferenceContext === 'moldDetail' ? 'moldOrdersInMoldPage' : 'moldOrders';
  const savedViewMode = contextPreferences?.moldOrderListView;
  const savedVisibleColumns = contextPreferences?.moldOrderTableVisibleColumns;
  
  const { viewMode, setViewMode } = useViewMode(savedViewMode ?? 'card');
  const [filteredSortedMoldOrders, setFilteredSortedMoldOrders] = useState<MoldOrder[]>(moldOrders);
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  const mobileActionsRef = useRef<HTMLDivElement>(null);
  
  // Initialize visibleColumns with direct function call to avoid hook order issues
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    const columns = getMoldOrdersTableColumns({
      companyCode,
      getMoldOrderTypeLabel,
    }); // Get customer-specific columns
    const defaultVisibleColumns = getDefaultVisibleColumnsForTable(companyCode, tableTypeConfig);
    
    // Create default visibility based on customer configuration
    const defaultVisibility = columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.key]: defaultVisibleColumns[column.key] || false,
      }),
      {},
    );
    
    // If we have saved preferences, filter them to only include columns that exist in current customer config
    if (savedVisibleColumns) {
      const filteredPreferences: Record<string, boolean> = {};
      columns.forEach(column => {
        const savedVisibility = savedVisibleColumns[column.key];
        filteredPreferences[column.key] = savedVisibility !== undefined ? savedVisibility : false;
      });
      return filteredPreferences;
    }
    
    return defaultVisibility;
  });
  
  // Memoize table columns to avoid recalculation - moved after all useState calls
  const tableColumns = useMemo(() => {
    const columns = getMoldOrdersTableColumns({
      getStatusText,
      statusFilterable: true,
      companyCode,
      getMoldOrderTypeLabel,
    });
    if (!serverMode) return columns;

    // Server mode (B5/B6): per-column filter popovers move to FilterBar (server filters), and
    // only the 7 keys the search endpoint can sort in SQL stay sortable — derived/close-out
    // columns (operationDate, operationDuration, rejectReason*, ...) become unsortable since
    // they were computed client-side from stateInstances/orderDetail.
    return columns.map((column): GenericColumn<MoldOrder> => ({
      ...column,
      filterable: false,
      sortable: Boolean(COLUMN_KEY_TO_SERVER_SORT[column.key]),
    }));
  }, [getStatusText, companyCode, getMoldOrderTypeLabel, serverMode]);
  const columnGroups = useMemo(() => getMoldOrdersColumnGroups({
    getStatusText,
    companyCode,
    getMoldOrderTypeLabel,
  }), [getStatusText, companyCode]);

  // Helper function to update preferences based on context
  const updateContextPreference = (updates: {
    moldOrderListView?: ViewMode;
    moldOrderTableVisibleColumns?: Record<string, boolean>;
  }) => {
    if (preferenceContext === 'moldDetail') {
      const updatedVisibleColumns = updates.moldOrderTableVisibleColumns ?? preferences.moldPage?.moldOrderTableVisibleColumns;
      updatePreference({ 
        moldPage: {
          moldOrderListView: updates.moldOrderListView ?? preferences.moldPage?.moldOrderListView ?? 'card',
          ...(updatedVisibleColumns ? { moldOrderTableVisibleColumns: updatedVisibleColumns } : {}),
        }
      });
    } else {
      updatePreference(updates);
    }
  };

  // Add handler for visible columns change
  const handleVisibleColumnsChange = (newVisibleColumns: Record<string, boolean>) => {
    // Only save preferences for columns that actually exist in the current customer configuration
    const validVisibleColumns: Record<string, boolean> = {};
    tableColumns.forEach(column => {
      if (newVisibleColumns[column.key] !== undefined) {
        validVisibleColumns[column.key] = newVisibleColumns[column.key];
      }
    });
    
    setVisibleColumns(validVisibleColumns);
    updateContextPreference({ moldOrderTableVisibleColumns: validVisibleColumns });
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    updateContextPreference({ moldOrderListView: mode });
  };

  // Defer the search term so typing stays responsive while filtering ~12k orders.
  const deferredSearchTerm = useDeferredValue(searchTerm);

  // Memoize filtered data to prevent unnecessary recalculations. In server mode, `moldOrders`
  // is already the current server-filtered/searched page (B1 moved to the server) — skip the
  // client `.filter()` entirely so a stale client search term can't hide server-matched rows.
  const filteredMoldOrders = useMemo(() => {
    if (serverMode) return moldOrders;
    if (!deferredSearchTerm.trim()) {
      return moldOrders;
    }

    return moldOrders.filter(order =>
      searchMoldOrder(order, deferredSearchTerm, selectedSearchField)
    );
  }, [moldOrders, deferredSearchTerm, selectedSearchField, serverMode]);

  const handleDataChange = useCallback((data: MoldOrder[]) => {
    setFilteredSortedMoldOrders(data);
  }, []);

  // Update filteredSortedMoldOrders when in card view mode
  useEffect(() => {
    if (viewMode === 'card') {
      // Server mode: the page is already ordered by the server (card view forces
      // sortBy=priority via the container/hook, B2) — re-sorting one page client-side is
      // harmless and keeps this branch identical either way.
      setFilteredSortedMoldOrders(sortByPriority(filteredMoldOrders));
    }
  }, [viewMode, filteredMoldOrders]);

  // Server mode (decision #2, B3): the download button links to the server-generated CSV of
  // the CURRENT filters/sort/q (paging ignored) instead of the client downloadCSV(...) path,
  // which only ever had the current page's rows loaded.
  const handleDownloadCSV = () => {
    if (serverMode) {
      if (serverMode.downloadingCsv) return;
      serverMode.onDownloadCsv();
      return;
    }
    downloadCSV(
      filteredSortedMoldOrders,
      visibleColumns,
      tableColumns,
      'mold_orders.csv'
    );
  };

  const filterButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isMobileActionsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        mobileActionsRef.current &&
        !mobileActionsRef.current.contains(event.target as Node)
      ) {
        setIsMobileActionsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileActionsOpen]);

  // Server mode always has a left-aligned result count, so the toolbar splits left/right.
  const flexStyle = serverMode || CustomHeader ? 'justify-between' : 'justify-end';
  const hidePaddingClass = hidePadding ? '' : 'px-4 sm:px-6';

  // Fit-to-viewport (server mode + fillHeight, lg+ only). `fit` drives the outer flex chain that
  // makes the card fill the viewport; `fitTable` additionally makes the table body the internal
  // scroller (the card view scrolls itself via MoldOrderCardView's own height measurement).
  const fit = Boolean(serverMode && fillHeight);
  const fitTable = fit && viewMode === 'table';
  const fitClass = fit ? 'lg:flex-1 lg:min-h-0' : '';

  const buttonGroup = (
    <ButtonGroup
      viewMode={viewMode}
      setViewMode={handleViewModeChange}
      onDownload={handleDownloadCSV}
      setIsColumnSelectorOpen={setIsColumnSelectorOpen}
      filterButtonRef={filterButtonRef}
      showSettings={true}
      isDownloading={serverMode?.downloadingCsv}
    />
  );

  const mobileActionsMenu = (
    <div className="relative md:hidden" ref={mobileActionsRef}>
      <button
        type="button"
        aria-label="เมนูเครื่องมือรายการ"
        aria-expanded={isMobileActionsOpen}
        onClick={() => setIsMobileActionsOpen((open) => !open)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>
      {isMobileActionsOpen && (
        <div className="absolute right-0 top-full z-30 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
          {viewMode === 'table' && (
            <button
              type="button"
              onClick={() => {
                setIsColumnSelectorOpen(true);
                setIsMobileActionsOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              <Settings className="h-4 w-4 text-gray-500" />
              เลือกคอลัมน์ที่แสดง
            </button>
          )}
          <button
            type="button"
            disabled={serverMode?.downloadingCsv}
            aria-busy={serverMode?.downloadingCsv}
            onClick={() => {
              handleDownloadCSV();
              setIsMobileActionsOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            {serverMode?.downloadingCsv ? (
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4 text-gray-500" />
            )}
            {serverMode?.downloadingCsv ? 'กำลังดาวน์โหลด...' : 'ส่งออก CSV'}
          </button>
          <div className="mt-2 grid grid-cols-2 overflow-hidden rounded-md border border-gray-200">
            <button
              type="button"
              onClick={() => {
                handleViewModeChange('card');
                setIsMobileActionsOpen(false);
              }}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 text-sm ${
                viewMode === 'card'
                  ? 'bg-slate-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Grid className="h-4 w-4" />
              การ์ด
            </button>
            <button
              type="button"
              onClick={() => {
                handleViewModeChange('table');
                setIsMobileActionsOpen(false);
              }}
              className={`flex items-center justify-center gap-1.5 border-l border-gray-200 px-3 py-2 text-sm ${
                viewMode === 'table'
                  ? 'bg-slate-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="h-4 w-4" />
              ตาราง
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`flex flex-col ${serverMode ? '' : `bg-${bgColorCode}-50 min-h-screen max-h-screen`} ${fitClass}`}>
      {/* Main content container. Server mode is full-width (aligns with the filter panel) and
          lets MainContainer own the scroll, instead of nesting a max-h-screen box. */}
      <div className={`flex flex-col w-full ${serverMode ? '' : 'overflow-hidden max-w-7xl mx-auto'} ${hidePaddingClass} ${fitClass}`}>
        {/* Server mode: group the controls + table in one card so it matches the filter panel
            and the toolbar reads as table controls rather than floating in empty space.
            fit: the card becomes a flex column that fills the viewport (table body scrolls). */}
        <div className={serverMode ? `rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden ${fit ? 'lg:flex lg:flex-col lg:flex-1 lg:min-h-0' : ''}` : ''}>
        <div className={`flex items-center gap-2 ${serverMode ? 'px-3 border-b border-gray-100' : `${flexStyle} flex-1 py-2`}`}>
          {serverMode ? (
            // One control bar: range + per-page on the left, page nav centered, actions right.
            <div data-testid="results-count" className="flex w-full items-center">
              <PaginationControls
                compact
                loading={serverMode.loading}
                rightSlot={
                  <>
                    <div className="hidden md:block">{buttonGroup}</div>
                    {mobileActionsMenu}
                  </>
                }
                currentPage={serverMode.page}
                pageSize={serverMode.pageSize}
                totalPages={Math.max(1, Math.ceil(serverMode.total / serverMode.pageSize))}
                totalItems={serverMode.total}
                onPageChange={serverMode.onPageChange}
                onPageSizeChange={serverMode.onPageSizeChange}
              />
            </div>
          ) : (
            <>
              {CustomHeader && <CustomHeader />}
              {buttonGroup}
            </>
          )}
        </div>

        {/* overflow-x-auto (not overflow-auto) on the table wrapper: horizontal overflow stays
            isolated to the table, but vertical scrolling flows to the page below lg (no nested
            vertical scroll). On lg+ fitTable, TableComponent owns the internal scroll via fillHeight. */}
        {viewMode === 'table' ? (
          <div className={`overflow-x-auto relative z-10 ${serverMode ? '' : 'mb-10'} ${fitTable ? 'lg:overflow-visible lg:flex lg:flex-col lg:flex-1 lg:min-h-0' : ''}`}>
            <div className={`w-full ${serverMode ? '' : 'pb-10'} ${fitTable ? 'lg:flex lg:flex-col lg:flex-1 lg:min-h-0' : ''}`}>
              <TableComponent<MoldOrder>
                data={filteredMoldOrders}
                columns={tableColumns}
                visibleColumns={visibleColumns}
                getRowHref={(order) => `/mold-orders/${order.id}?source=list`}
                onRowClick={(order, event) => {
                  const href = `/mold-orders/${order.id}?source=list`;
                  if (event?.ctrlKey || event?.metaKey || event?.shiftKey) {
                    window.open(href, '_blank', 'noopener,noreferrer');
                  } else {
                    navigate(href);
                  }
                }}
                onDataChange={handleDataChange}
                pagination={!serverMode}
                savePreferenceSettings={{
                  // Server mode: don't persist/restore client filter/sort state for this
                  // table — stale client filters could otherwise silently hide rows of a
                  // server-fetched page (plan §5.5).
                  isEnable: !serverMode,
                  key: 'moldOrderTable',
                }}
                {...(serverMode
                  ? {
                      externalPagination: {
                        currentPage: serverMode.page,
                        pageSize: serverMode.pageSize,
                        onPageChange: serverMode.onPageChange,
                        onPageSizeChange: serverMode.onPageSizeChange,
                      },
                      totalItemsOverride: serverMode.total,
                      serverSort: {
                        key: SERVER_SORT_TO_COLUMN_KEY[serverMode.sortBy],
                        dir: serverMode.sortDir,
                        onChange: (key: string, dir: 'asc' | 'desc') => {
                          const sortBy = COLUMN_KEY_TO_SERVER_SORT[key];
                          if (sortBy) serverMode.onSortChange(sortBy, dir);
                        },
                      },
                      defaultSortKey: '',
                      density: 'compact' as const,
                      fillHeight: fitTable,
                      loading: serverMode.loading,
                      emptyState: (
                        <div className="flex flex-col items-center gap-3 py-8">
                          {serverMode.hasActiveFilters ? (
                            <SearchX className="h-10 w-10 text-gray-300" />
                          ) : (
                            <Inbox className="h-10 w-10 text-gray-300" />
                          )}
                          <span className="text-sm text-gray-500">
                            {serverMode.hasActiveFilters
                              ? 'ไม่พบใบงานที่ตรงกับตัวกรอง'
                              : 'ไม่มีใบงาน'}
                          </span>
                          {serverMode.hasActiveFilters &&
                            serverMode.onClearFilters && (
                              <button
                                type="button"
                                onClick={serverMode.onClearFilters}
                                className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-100"
                              >
                                ล้างตัวกรองทั้งหมด
                              </button>
                            )}
                        </div>
                      ),
                    }
                  : {})}
              />
            </div>
          </div>
        ) : (
          // No overflow-auto here — the virtualized List owns its own scrolling. The pager lives
          // in the shared toolbar above, so the card view is just the list.
          <div className={`relative z-10 ${fit ? 'lg:flex lg:flex-col lg:flex-1 lg:min-h-0' : ''}`}>
            <MoldOrderCardView moldOrders={filteredMoldOrders} pageScroll={Boolean(serverMode)} />
          </div>
        )}
        </div>
      </div>

      {/* Column Selector Modal */}
      <ColumnSelector
        isOpen={isColumnSelectorOpen}
        onClose={() => setIsColumnSelectorOpen(false)}
        columns={tableColumns}
        columnGroups={columnGroups}
        visibleColumns={visibleColumns}
        setVisibleColumns={handleVisibleColumnsChange}
      />
    </div>
  );
}

export default MoldOrderList;
